import React, { useState, useRef, useEffect, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { streamGraphQuery } from '../../services/graphApi';
import { MarkdownContent, formatTime, truncate, generateId } from './chatMarkdown';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface Message {
  id: string;
  role: 'user' | 'ai';
  content: string;
  timestamp: Date;
}

interface ChatSession {
  conversationId: string;
  /** First user question — shown as preview in the history list. */
  preview: string;
  startedAt: string;
  messages: Message[];
}

interface ChatDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  quoteId: string;
  companyName?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Quick-question suggestions shown on the empty state screen
// ─────────────────────────────────────────────────────────────────────────────

const SUGGESTIONS = [
  "Which driver's MVR is currently missing?",
  "Are the provided MVRs issued within the last month?",
  "Do any of the drivers have a history of DUIs or other major violations?",
  "How long has this business been established?",
  "What specific commodities do they haul?",
  "What is the garaging/operating state for this insured?",
  "Do we have the loss runs from 2023?"
];

// ─────────────────────────────────────────────────────────────────────────────
// Message bubble
// ─────────────────────────────────────────────────────────────────────────────

const Bubble: React.FC<{ msg: Message }> = ({ msg }) => (
  <motion.div
    className={`qd-chat-msg qd-chat-msg--${msg.role}`}
    initial={{ opacity: 0, y: 6 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.18 }}
  >
    {msg.role === 'ai' ? (
      <div className="qd-chat-bubble"><MarkdownContent text={msg.content} /></div>
    ) : (
      <div className="qd-chat-bubble">{msg.content}</div>
    )}
    <span className="qd-chat-time">{formatTime(msg.timestamp)}</span>
  </motion.div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Typing indicator
// ─────────────────────────────────────────────────────────────────────────────

const Typing: React.FC = () => (
  <motion.div className="qd-chat-msg qd-chat-msg--ai"
    initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
    <div className="qd-chat-typing">
      <span className="qd-chat-dot" />
      <span className="qd-chat-dot" />
      <span className="qd-chat-dot" />
    </div>
  </motion.div>
);

// ─────────────────────────────────────────────────────────────────────────────
// ChatDrawer
// ─────────────────────────────────────────────────────────────────────────────

const ChatDrawer: React.FC<ChatDrawerProps> = ({ isOpen, onClose, quoteId, companyName }) => {
  // Active conversation
  const [conversationId, setConversationId] = useState<string>(generateId);
  const [messages, setMessages]             = useState<Message[]>([]);
  const [input, setInput]                   = useState('');
  const [isStreaming, setIsStreaming]        = useState(false);
  const [isThinking, setIsThinking]         = useState(false);

  // Past conversations
  const [sessions, setSessions]         = useState<ChatSession[]>([]);
  const [showHistory, setShowHistory]   = useState(false);

  const bottomRef   = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const abortRef    = useRef<AbortController | null>(null);

  // Scroll to bottom on new messages or thinking state
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isThinking]);

  // ── New Chat ──────────────────────────────────────────────────────────────

  const handleNewChat = useCallback(() => {
    abortRef.current?.abort();

    // Archive the current conversation if it has any messages
    if (messages.length > 0) {
      const firstQuestion = messages.find((m) => m.role === 'user')?.content ?? '';
      setSessions((prev) => [
        { conversationId, preview: truncate(firstQuestion), startedAt: new Date().toISOString(), messages },
        ...prev,
      ]);
    }

    setConversationId(generateId());
    setMessages([]);
    setInput('');
    setIsStreaming(false);
    setIsThinking(false);
    setShowHistory(false);

    setTimeout(() => textareaRef.current?.focus(), 100);
  }, [conversationId, messages]);

  // ── Switch to a previous conversation ────────────────────────────────────

  const handleSwitchSession = useCallback((session: ChatSession) => {
    abortRef.current?.abort();

    // Archive current session before switching (if it has messages)
    if (messages.length > 0) {
      const firstQuestion = messages.find((m) => m.role === 'user')?.content ?? '';
      setSessions((prev) => {
        if (prev.some((s) => s.conversationId === conversationId)) return prev;
        return [
          { conversationId, preview: truncate(firstQuestion), startedAt: new Date().toISOString(), messages },
          ...prev,
        ];
      });
    }

    setConversationId(session.conversationId);
    setMessages(session.messages);
    setInput('');
    setIsStreaming(false);
    setIsThinking(false);
    setShowHistory(false);
  }, [conversationId, messages]);

  // ── Send message ──────────────────────────────────────────────────────────

  const sendMessage = useCallback((question: string) => {
    const text = question.trim();
    if (!text || isStreaming) return;

    abortRef.current?.abort();

    const userMsg: Message = {
      id: `u-${Date.now()}`,
      role: 'user',
      content: text,
      timestamp: new Date(),
    };
    const aiId = `a-${Date.now()}`;

    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setIsThinking(true);
    setIsStreaming(true);
    if (textareaRef.current) textareaRef.current.style.height = 'auto';

    abortRef.current = streamGraphQuery(
      quoteId,
      text,
      conversationId,
      (token) => {
        setIsThinking(false);
        setMessages((prev) => {
          const exists = prev.find((m) => m.id === aiId);
          if (exists) {
            return prev.map((m) => m.id === aiId ? { ...m, content: m.content + token } : m);
          }
          return [...prev, { id: aiId, role: 'ai', content: token, timestamp: new Date() }];
        });
      },
      () => {
        setIsThinking(false);
        setIsStreaming(false);
      },
      (errMsg) => {
        setIsThinking(false);
        setIsStreaming(false);
        setMessages((prev) => [...prev, {
          id: `err-${Date.now()}`,
          role: 'ai',
          content: `⚠️ ${errMsg}`,
          timestamp: new Date(),
        }]);
      }
    );
  }, [quoteId, conversationId, isStreaming]);

  // ── Input handlers ────────────────────────────────────────────────────────

  const onInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 100)}px`;
  };

  const handleSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault();
    sendMessage(input);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const handleClose = () => {
    abortRef.current?.abort();
    onClose();
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────

  const isEmpty = messages.length === 0 && !isThinking;

  const panelVariants = {
    hidden:  { x: '100%', opacity: 0.6 },
    visible: { x: 0, opacity: 1, transition: { type: 'spring' as const, damping: 28, stiffness: 260 } },
    exit:    { x: '100%', opacity: 0, transition: { duration: 0.2, ease: 'easeIn' as const } },
  };

  const overlayVariants = {
    hidden:  { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.2 } },
    exit:    { opacity: 0, transition: { duration: 0.15 } },
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="qd-overlay qd-chat-overlay"
            variants={overlayVariants}
            initial="hidden" animate="visible" exit="exit"
            onClick={handleClose}
          />

          {/* Panel */}
          <motion.div
            className="qd-panel qd-chat-panel"
            variants={panelVariants}
            initial="hidden" animate="visible" exit="exit"
            role="dialog"
            aria-label="Carrier AI Chat"
            aria-modal="true"
          >
            {/* ── Header ────────────────────────────────────────────────── */}
            <div className="qd-header">
              <div className="qd-header__left" style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                <div className="qd-chat-header__icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                </div>
                <div>
                  <div className="qd-chat-header__title">Carrier AI Assistant</div>
                  <div className="qd-chat-header__sub">{companyName || `Quote ${quoteId}`}</div>
                </div>
              </div>

              <div className="qd-header__right" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {/* History toggle */}
                <button
                  className="qd-close-btn"
                  title="Chat history"
                  aria-label="Show chat history"
                  onClick={() => setShowHistory((v) => !v)}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 8v4l3 3" />
                    <path d="M3.05 11a9 9 0 1 0 .5-3" />
                    <polyline points="3 4 3 11 10 11" />
                  </svg>
                </button>

                {/* New Chat */}
                <button
                  className="qd-chat-new-btn"
                  title="Start a new conversation"
                  aria-label="New chat"
                  disabled={isStreaming}
                  onClick={handleNewChat}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  New Chat
                </button>

                {/* Close */}
                <button className="qd-close-btn" onClick={handleClose} aria-label="Close">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            </div>

            {/* ── History panel ────────────────────────────────────────── */}
            <AnimatePresence>
              {showHistory && (
                <motion.div
                  className="qd-chat-history"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="qd-chat-history__header">
                    <span>Past Conversations</span>
                  </div>
                  {sessions.length === 0 ? (
                    <p className="qd-chat-history__empty">No previous conversations.</p>
                  ) : (
                    <ul className="qd-chat-history__list">
                      {sessions.map((s) => (
                        <li
                          key={s.conversationId}
                          className={`qd-chat-history__item${s.conversationId === conversationId ? ' qd-chat-history__item--active' : ''}`}
                          onClick={() => handleSwitchSession(s)}
                        >
                          <span className="qd-chat-history__preview">
                            {s.preview || 'Conversation'}
                          </span>
                          <span className="qd-chat-history__time">
                            {formatTime(new Date(s.startedAt))}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            {/* ── Messages ─────────────────────────────────────────────── */}
            <div className="qd-body qd-chat-messages">
              {isEmpty ? (
                <div className="qd-chat-welcome">
                  <div className="qd-chat-welcome__icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                    </svg>
                  </div>
                  <div className="qd-chat-welcome__title">Ask about this carrier</div>
                  <div className="qd-chat-welcome__text">
                    I have access to all uploaded documents — MVR, Loss Run, CAB, and SAFER data.
                  </div>
                  <div className="qd-chat-suggestions">
                    {SUGGESTIONS.map((q) => (
                      <button key={q} className="qd-chat-suggestion" onClick={() => sendMessage(q)}>
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  {messages.map((msg) => <Bubble key={msg.id} msg={msg} />)}
                  {isThinking && <Typing />}
                </>
              )}
              <div ref={bottomRef} />
            </div>

            {/* ── Input ────────────────────────────────────────────────── */}
            <div className="qd-chat-input-area">
              <form onSubmit={handleSubmit}>
                <div className="qd-chat-input-row">
                  <textarea
                    ref={textareaRef}
                    className="qd-chat-textarea"
                    placeholder="Ask about drivers, claims, safety, verdict…"
                    value={input}
                    onChange={onInputChange}
                    onKeyDown={handleKeyDown}
                    rows={1}
                    disabled={isStreaming}
                  />
                  <button
                    type="submit"
                    className="qd-chat-send"
                    disabled={!input.trim() || isStreaming}
                    aria-label="Send"
                  >
                    {isStreaming ? (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" />
                      </svg>
                    ) : (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
                      </svg>
                    )}
                  </button>
                </div>
              </form>
              <div className="qd-chat-hint">Enter to send · Shift+Enter for new line</div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ChatDrawer;
